import { useMemo } from "react";

import dateFormat from "dateformat";
import { Box } from "@mui/material";

import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Download2LineIcon from "remixicon-react/Download2LineIcon";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import GenericStatusHistoryButton from "$components/GenericStatusHistoryButton";
import { BtnPoper, Button, Text } from "$components";
import { getShippingRecordStatusLabelByRoleAndStatus } from "$utils/typeConstants";
import {
  ShippingRecord,
  PreparationOrderStatus,
  ShippingRecordStatus,
} from "$types";
import useAuth from "$hooks/useAuth";
import useShippingRecordExport from "$hooks/useShippingRecordExport";
import { useStyles } from "./ShippingRecordDetails.styles";
import ShippingRecordShareButton from "../ShippingPublic/ShippingRecordShareButton";

export function HeaderShippingRecordsDetails({
  shippingRecordId,
  shippingRecord,
  updateShippingRecord,
  isFetchingShippingRecord,
  toggleHistoryDrawer,
  toggleCancelShippingRecordModal,
  isShippingRecordCanceled,
}: {
  shippingRecordId: string;
  shippingRecord: ShippingRecord | null;
  updateShippingRecord: (shippingRecordId: string, data: any) => Promise<any>;
  isFetchingShippingRecord: boolean;
  toggleHistoryDrawer: (open: boolean) => void;
  toggleCancelShippingRecordModal: (open: boolean) => void;
  isShippingRecordCanceled: boolean;
}) {
  const classes = useStyles();
  const { userRole } = useAuth();

  const { exportShippingRecord, exportShippingRecordAffretement } =
    useShippingRecordExport(shippingRecord);

  const isLinkedToExternalConnector = shippingRecord?.shipments?.some(
    (shipment) => shipment.connectorOrderId
  );

  const getPublicURL = () =>
    `${window.location.origin}/dossier-expedition/${shippingRecordId}`;

  const handleStatusUpdate = async (
    selectedStatus: ShippingRecordStatus,
    statusHistory: { value: ShippingRecordStatus; date: string }[]
  ) => {
    await updateShippingRecord(shippingRecordId, {
      ...shippingRecord,
      status: selectedStatus,
      statusHistory: shippingRecord.statusHistory.map((history) => {
        const foundStatusHistory = statusHistory?.find(
          (h) => h.value === history.status
        );
        if (!foundStatusHistory) {
          return history;
        }
        return {
          ...history,
          date: foundStatusHistory.date,
          status: foundStatusHistory.value,
        };
      }),
    });
  };

  const statusHistory = useMemo(
    () =>
      Object.keys(ShippingRecordStatus).map((statusKey) => {
        const statusHistory = shippingRecord?.statusHistory?.find(
          (history) => history.status === ShippingRecordStatus[statusKey]
        );
        const date =
          statusKey === ShippingRecordStatus.TO_SHIP
            ? statusHistory?.date ||
              statusHistory?.updatedAt ||
              statusHistory?.createdAt
            : statusHistory?.date;
        return {
          value: ShippingRecordStatus[statusKey],
          label: getShippingRecordStatusLabelByRoleAndStatus(
            userRole,
            ShippingRecordStatus[statusKey]
          ),
          date,
        };
      }),
    [shippingRecord?.statusHistory, userRole]
  );

  // Min date for "A expédier" (TO_SHIP) of ShippingRecord: max of OPs' IN_PROGRESS dates. Normalize to start of day local so the whole day is selectable.
  const toShipMinDate = useMemo(() => {
    const dates: Date[] = [];
    shippingRecord?.shipments?.forEach((shipment) => {
      const op = shipment?.op;
      const inProgressEntry = op?.statusHistory?.find(
        (h: { status: number }) =>
          h.status === PreparationOrderStatus.IN_PROGRESS
      );
      const d =
        inProgressEntry?.statusDate ??
        (inProgressEntry as { date?: string } | undefined)?.date;
      if (d) dates.push(new Date(d));
    });
    if (dates.length === 0) return null;
    const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
    // Start of that calendar day in local time so the datepicker allows the whole day
    const minDate = new Date(
      maxDate.getFullYear(),
      maxDate.getMonth(),
      maxDate.getDate()
    );
    return minDate;
  }, [shippingRecord?.shipments]);

  return (
    <div className={classes.headerContainer}>
      <div className={classes.customHeader}>
        <div className={classes.calendar}>
          <CalendarCheckFillIcon className={classes.calendarIcon} />
          Créé le {dateFormat(shippingRecord?.createdAt, DEFAULT_DATE_FORMAT)}
        </div>
        <div className={classes.badge}>
          <GenericStatusHistoryButton
            disabled={isShippingRecordCanceled}
            isFetching={isFetchingShippingRecord}
            currentStatus={shippingRecord?.status}
            statusHistory={statusHistory}
            hiddenValues={[
              ShippingRecordStatus.CANCELED,
              ShippingRecordStatus.DRAFT,
            ]}
            lockedValues={
              isLinkedToExternalConnector
                ? Object.keys(ShippingRecordStatus).filter(
                    (status) => status !== ShippingRecordStatus.COLLECTED
                  )
                : [ShippingRecordStatus.CANCELED, ShippingRecordStatus.DRAFT]
            }
            alwaysEditableValues={
              isLinkedToExternalConnector
                ? [ShippingRecordStatus.COLLECTED]
                : []
            }
            minDateByStatus={{
              [ShippingRecordStatus.TO_SHIP]: toShipMinDate,
            }}
            updateStatus={handleStatusUpdate}
          />
        </div>
        <div>
          {shippingRecord?.shippingRecordDisputes?.some(
            (dispute) => !dispute.closedAt
          ) ? (
            <SentimentVeryDissatisfiedIcon style={{ color: "tomato" }} />
          ) : (
            <SentimentSatisfiedAltIcon style={{ color: "green" }} />
          )}
        </div>
      </div>
      <div className={classes.lineBlockContainer}>
        <div className={classes.cancelButtonContainer}>
          <ShippingRecordShareButton hasLabel={false} url={getPublicURL()} />
        </div>
        <div className={classes.cancelButtonContainer}>
          <BtnPoper
            BtnComponent={Button}
            BtnProps={{ disabled: isShippingRecordCanceled }}
            BtnLabel={
              <>
                <Download2LineIcon style={{ marginRight: 8 }} />
                Exporter le dossier
                <ExpandMoreIcon style={{ marginLeft: 8 }} />
              </>
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Button
                variant="contained"
                className={`${classes.textButton} ${classes.exportMenuItem}`}
                onClick={exportShippingRecord}
              >
                Dossier standard
              </Button>
              <Button
                variant="contained"
                className={`${classes.textButton} ${classes.exportMenuItem}`}
                onClick={exportShippingRecordAffretement}
              >
                Dossier d&apos;affrètement
              </Button>
            </Box>
          </BtnPoper>
        </div>
        <div className={classes.cancelButtonContainer}>
          <Button variant="outlined" onClick={() => toggleHistoryDrawer(true)}>
            <VisibilityOutlinedIcon style={{ marginRight: 8 }} />
            {`Voir l'historique`}
          </Button>
        </div>
        <div className={classes.cancelButtonContainer}>
          {isShippingRecordCanceled ? (
            <Text type="disabled">{`Dossier annulé le ${dateFormat(
              shippingRecord?.statusHistory?.find(
                (history) => history.status === ShippingRecordStatus.CANCELED
              )?.updatedAt,
              DEFAULT_DATE_FORMAT
            )}`}</Text>
          ) : (
            <Button
              disabled={shippingRecord?.status !== ShippingRecordStatus.TO_SHIP}
              onClick={() => toggleCancelShippingRecordModal(true)}
            >
              Annuler le dossier
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
